import { useEffect, useRef, useState, type RefObject } from "react";
import type { MascotMode } from "./LoginArt";

/**
 * The login-page story:
 *   1. he walks in from the left edge of the orange panel,
 *   2. stops, waves and says hi,
 *   3. walks on to the right edge of the orange panel,
 *   4. leaps over to the login card and ends up leaning on its top edge,
 *   5. from then on he reacts to the form (email / password / error / loading).
 * Plays every time the login page opens (reduced-motion users just get the final pose).
 * Desktop only (the orange panel is hidden below `lg`).
 */

const ASSETS = `${import.meta.env.BASE_URL}assets/mascot/`;


// Sprite sizes (px, as exported) and the x of the head — all poses are aligned by the head.
const SPRITES = {
  w1: { w: 397, h: 663, ax: 177 },
  w2: { w: 397, h: 663, ax: 177 },
  w3: { w: 397, h: 663, ax: 177 },
  w4: { w: 397, h: 663, ax: 177 },
  wave: { w: 443, h: 856, ax: 212 },
  lean: { w: 342, h: 860, ax: 184 },
} as const;
type Pose = keyof typeof SPRITES;
const FILES: Record<Pose, string> = { w1: "walk1", w2: "walk2", w3: "walk3", w4: "walk4", wave: "wave", lean: "lean" };

/** Order the four walk frames are played in. Swap numbers here if a step looks off. */
const WALK_ORDER: Pose[] = ["w3", "w4", "w2", "w1"];
/** Story pace: 1 = this speed, 2 = twice as slow. */
const SLOW = 1;
const STEP_MS = 210; // time per walk frame
const K = 0.226; // px per sprite px while walking (walk frame ≈ 150px tall)
const BASE = 1.45; // walking / waving size (× sprite size)
const FINAL_H = 0.62; // final pose height as a share of the whole login card height

const ease = (p: number) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);
const lerp = (a: number, b: number, p: number) => a + (b - a) * p;

const IDLE_LINES = ["Ready to log in? 😊", "Welcome back!", "Psst… tap me 👋"];
const MODE_LINE: Partial<Record<MascotMode, string>> = {
  email: "Type your school email ✉️",
  password: "No peeking! 🙈",
  peek: "Okay, I'm looking 👀",
  loading: "Checking… ⏳",
  error: "Oops! Check your details 😅",
};

const CSS = `
.lgs-fig { position: absolute; left: 0; top: 0; width: 0; height: 0; will-change: transform; }
.lgs-fig img { position: absolute; bottom: 0; display: block; user-select: none; -webkit-user-drag: none; visibility: hidden; max-width: none; }
.lgs-shadow { position: absolute; left: -46px; top: -6px; width: 92px; height: 12px; border-radius: 50%; background: rgba(60, 20, 5, .28); filter: blur(4px); }
.lgs-inner { position: absolute; left: 0; top: 0; width: 0; height: 0; transform-origin: 0 0; }
.lgs-bubble { position: absolute; left: 0; top: 0; width: max-content; max-width: 11.5rem; padding: .45rem .8rem; border-radius: 1rem; background: #fff; color: #7c2d12; font: 600 .78rem/1.25 system-ui, sans-serif; box-shadow: 0 10px 24px -10px rgba(124, 45, 18, .55); z-index: 2; }
.lgs-bubble::after { content: ""; position: absolute; bottom: -5px; width: 12px; height: 12px; background: #fff; transform: rotate(45deg); border-radius: 2px; }
.lgs-bubble.l { transform: translate(calc(-100% - var(--hw, 80px) * .32), calc(var(--hh, 190px) * -.86)); border-bottom-right-radius: .25rem; }
.lgs-bubble.l::after { right: 16px; }
.lgs-bubble.r { transform: translate(calc(var(--hw, 80px) * .32), calc(var(--hh, 190px) * -.8)); border-bottom-left-radius: .25rem; }
.lgs-bubble.r::after { left: 16px; }
.lgs-bubble > span { display: inline-block; }
@media (prefers-reduced-motion: no-preference) {
  @keyframes lgs-sway { 0%,100% { transform: rotate(-.8deg) } 50% { transform: rotate(.8deg) } }
  @keyframes lgs-breathe { 0%,100% { transform: scaleY(1) } 50% { transform: scaleY(1.012) } }
  @keyframes lgs-shake { 0%,100% { transform: translateX(0) rotate(0) } 18% { transform: translateX(-8px) rotate(-2.5deg) } 40% { transform: translateX(7px) rotate(2deg) } 62% { transform: translateX(-5px) rotate(-1.2deg) } 82% { transform: translateX(2px) } }
  @keyframes lgs-hop { 0%,100% { transform: translateY(0) } 40% { transform: translateY(-16px) } 70% { transform: translateY(0) } 85% { transform: translateY(-4px) } }
  @keyframes lgs-pop { from { opacity: 0; transform: translateY(6px) scale(.9) } to { opacity: 1; transform: none } }
  .lgs-inner.idle { animation: lgs-sway 5s ease-in-out infinite; }
  .lgs-inner.is-error { animation: lgs-shake .5s ease-in-out 1; }
  .lgs-inner.is-peek { animation: lgs-hop .6s ease-out 1; }
  .lgs-inner.is-loading { animation: lgs-sway 1.1s ease-in-out infinite; }
  .lgs-bubble > span { animation: lgs-pop .3s ease-out both; }
}
`;

type Bubble = { text: string; side: "l" | "r" } | null;

export function StoryMascot({ mode, cardRef, leftRef, rightRef }: {
  mode: MascotMode;
  cardRef: RefObject<HTMLElement>;
  leftRef: RefObject<HTMLElement>;
  rightRef: RefObject<HTMLElement>;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const figRef = useRef<HTMLDivElement>(null);
  const bubRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<HTMLSpanElement>(null);
  const imgs = useRef<Partial<Record<Pose, HTMLImageElement | null>>>({});
  const doneRef = useRef(false);
  const waveUntil = useRef(0);
  const poseRef = useRef<Pose>("lean");

  const [done, setDone] = useState(false);
  const [bubble, setBubble] = useState<Bubble>(null);
  const [greet, setGreet] = useState(0);

  // ---- story timeline ----
  useEffect(() => {
    const fig = figRef.current;
    const bub = bubRef.current;
    if (!fig || !bub) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let cancelled = false;

    const geo = () => {
      const l = leftRef.current, c = cardRef.current;
      const lx = l?.offsetLeft ?? 12, lw = l?.offsetWidth ?? 0, lh = l?.offsetHeight ?? 0, lt = l?.offsetTop ?? 0;
      const ch = c?.offsetHeight ?? lh;
      const finalScale = (ch * FINAL_H) / (SPRITES.lean.h * K);
      const half = (SPRITES.lean.w * K * finalScale) / 2;
      // he ends up straddling the edge of the orange panel, on the login panel's side
      return { floor: lt + lh - 30, xStart: -110, xWave: lx + lw * 0.36, xEnd: lx + lw - 6 - half * 0.45 + 0, finalScale };
    };

    const setPose = (p: Pose) => {
      poseRef.current = p;
      (Object.keys(SPRITES) as Pose[]).forEach((k) => {
        const el = imgs.current[k];
        if (el) el.style.visibility = k === p ? "visible" : "hidden";
      });
    };
    const place = (x: number, y: number, s = 1, sx = 1, sy = 1, rot = 0) => {
      fig.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rot}deg) scale(${s * sx}, ${s * sy})`;
      bub.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      const sp = SPRITES[poseRef.current];
      bub.style.setProperty("--hh", `${sp.h * K * s}px`);
      bub.style.setProperty("--hw", `${sp.w * K * s}px`);
    };
    const rest = () => { const g = geo(); setPose(poseRef.current === "wave" && Date.now() < waveUntil.current ? "wave" : "lean"); place(g.xEnd, g.floor, g.finalScale); };
    const walkFrame = (now: number) => {
      const step = Math.floor(now / STEP_MS);
      setPose(WALK_ORDER[step % WALK_ORDER.length]);
      return -Math.abs(Math.sin((now / STEP_MS) * Math.PI / 2)) * 3; // little bounce per step
    };

    const finish = () => {
      doneRef.current = true;
      setBubble(null);
      rest();
      if (shadowRef.current) shadowRef.current.style.opacity = "1";
      setDone(true);
    };

    const onResize = () => { if (doneRef.current) rest(); };
    window.addEventListener("resize", onResize);

    const imgList = Object.values(imgs.current).filter(Boolean) as HTMLImageElement[];
    Promise.all(imgList.map((i) => (i.decode ? i.decode().catch(() => undefined) : undefined))).then(() => {
      if (cancelled) return;
      if (reduce) {
        // No story: he is just there, leaning on the card.
        finish();
        if (!reduce) { fig.animate([{ opacity: 0, transform: fig.style.transform + " translateY(10px)" }, { opacity: 1, transform: fig.style.transform }], { duration: 450, easing: "ease-out" }); }
        return;
      }

      const g0 = geo();
      const shadow = (v: number) => { if (shadowRef.current) shadowRef.current.style.opacity = String(v); };
      type Seg = { d: number; on?: () => void; f: (p: number, now: number) => void };
      const segs: Seg[] = [
        { // 1. walk in
          d: 4400 * SLOW,
          on: () => shadow(1),
          f: (p, now) => { const g = geo(); const b = walkFrame(now); place(lerp(g.xStart, g.xWave, Math.sin((p * Math.PI) / 2)), g.floor + b * BASE, BASE); },
        },
        { // 2. stop and wave
          d: 3800 * SLOW,
          on: () => { setPose("wave"); setBubble({ text: "Hi there! 👋", side: "l" }); },
          f: (p) => { const g = geo(); place(g.xWave, g.floor, BASE, 1, 1, Math.sin(p * Math.PI * 4) * 1.3); },
        },
        { // 3. walk on to the right end of the orange panel
          d: 5200 * SLOW,
          on: () => setBubble(null),
          f: (p, now) => { const g = geo(); const b = walkFrame(now); place(lerp(g.xWave, g.xEnd, ease(p) * 0.5 + p * 0.5), g.floor + b * BASE, lerp(BASE, g.finalScale, ease(Math.max(0, (p - 0.35) / 0.65)))); },
        },
        { // 4. stop and strike the leaning pose
          d: 800 * SLOW,
          on: () => { setPose("lean"); },
          f: (p) => { const g = geo(); const k = Math.sin(p * Math.PI); place(g.xEnd, g.floor, g.finalScale, 1 + 0.04 * k, 1 - 0.06 * k); },
        },
      ];

      // start off-screen on the left
      setPose("w3");
      place(g0.xStart, g0.floor, BASE);
      let i = 0, t0 = 0;
      const tick = (now: number) => {
        if (cancelled) return;
        if (!t0) { t0 = now; segs[0].on?.(); }
        const s = segs[i];
        const p = Math.min(1, (now - t0) / s.d);
        s.f(p, now);
        if (p >= 1) {
          i++;
          if (i >= segs.length) { finish(); return; }
          t0 = now;
          segs[i].on?.();
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    });

    return () => { cancelled = true; cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- after the story: idle chatter + reactions to the form ----
  useEffect(() => {
    if (!done) return;
    if (mode !== "idle") return;
    const id = window.setInterval(() => setGreet((g) => g + 1), 4800);
    return () => window.clearInterval(id);
  }, [done, mode]);

  useEffect(() => {
    if (!done) return;
    if (Date.now() < waveUntil.current) return; // a tap-wave is on screen
    setBubble({ text: MODE_LINE[mode] ?? IDLE_LINES[greet % IDLE_LINES.length], side: "l" });
  }, [done, mode, greet]);

  // Tap him and he waves back.
  function onTap() {
    if (!done) return;
    waveUntil.current = Date.now() + 1900;
    const el = imgs.current;
    if (el.lean) el.lean.style.visibility = "hidden";
    if (el.wave) el.wave.style.visibility = "visible";
    poseRef.current = "wave";
    setBubble({ text: "Hey! 👋", side: "l" });
    window.setTimeout(() => {
      if (Date.now() < waveUntil.current - 20) return;
      if (el.wave) el.wave.style.visibility = "hidden";
      if (el.lean) el.lean.style.visibility = "visible";
      poseRef.current = "lean";
      setBubble(null);
      setGreet((g) => g + 1); // re-run the bubble effect for the current mode
    }, 1950);
  }

  const cls = done ? (mode === "idle" ? "idle" : `is-${mode}`) : "";

  return (
    <div ref={stageRef} className="pointer-events-none absolute inset-0 z-30 hidden lg:block" style={{ clipPath: "inset(-400px -400px -400px 0)" }} aria-hidden>
      <style>{CSS}</style>
      <div ref={figRef} className="lgs-fig" style={{ transform: "translate3d(-200px, 0, 0)" }}>
        <div className={`lgs-inner ${cls}`}>
          <span ref={shadowRef} className="lgs-shadow" style={{ opacity: 0 }} />
          {(Object.keys(SPRITES) as Pose[]).map((k) => {
            const s = SPRITES[k];
            return (
              <img
                key={k}
                ref={(el) => { imgs.current[k] = el; }}
                src={`${ASSETS}${FILES[k]}.webp`}
                alt=""
                draggable={false}
                width={s.w * K}
                height={s.h * K}
                style={{ left: -s.ax * K, width: s.w * K, height: s.h * K, pointerEvents: done ? "auto" : "none", cursor: done ? "pointer" : "default" }}
                onClick={onTap}
              />
            );
          })}
        </div>
      </div>
      <div ref={bubRef} className="absolute left-0 top-0">
        {bubble && (
          <div className={`lgs-bubble ${bubble.side}`}>
            <span key={`${bubble.side}-${bubble.text}`}>{bubble.text}</span>
          </div>
        )}
      </div>
    </div>
  );
}